/**
 * The workspace a request works in: one of the organisations (tenants) the
 * person is a member of, with their team there (ADR 0113).
 *
 * Every request names it in `X-Rudi-Tenant`, so a person who works at the
 * Charité and in a Magdeburg research team reads and writes each team's work
 * from one account. Without the header the backend uses the default (earliest)
 * organisation, as it did before.
 */
export const WORKSPACE_HEADER = 'X-Rudi-Tenant';

/** The error code the backend answers for a tenant the caller is not a member of. */
export const WORKSPACE_UNAVAILABLE = 'workspace_unavailable';

/** One organisation the person works in, as `/auth/status` lists it. */
export interface Workspace {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  teamId?: string | null;
  teamName?: string | null;
  isTeamAdmin?: boolean;
  isDefault?: boolean;
}

let workspaceTenantId: string | null = null;
const unavailableListeners = new Set<(tenantId: string) => void>();

/** Make every following request name `tenantId`, or none with `null`. */
export function setWorkspaceTenantId(tenantId: string | null): void {
  workspaceTenantId = tenantId;
}

export function getWorkspaceTenantId(): string | null {
  return workspaceTenantId;
}

/** The headers a request outside the shared client adds to name the workspace. */
export function workspaceHeaders(
  tenantId: string | null = workspaceTenantId
): Record<string, string> {
  return tenantId ? { [WORKSPACE_HEADER]: tenantId } : {};
}

/**
 * Called when the backend refuses the named workspace, as after the person
 * left that organisation. The app then falls back to its default workspace.
 */
export function onWorkspaceUnavailable(listener: (tenantId: string) => void): () => void {
  unavailableListeners.add(listener);
  return () => {
    unavailableListeners.delete(listener);
  };
}

export function notifyWorkspaceUnavailable(tenantId: string): void {
  for (const listener of [...unavailableListeners]) listener(tenantId);
}

/** Whether a response body is the backend's refusal of the named workspace. */
export function isWorkspaceUnavailable(status: number | undefined, body: unknown): boolean {
  if (status !== 403 || typeof body !== 'object' || body === null) return false;
  const details = (body as { details?: unknown }).details;
  return (
    typeof details === 'object' &&
    details !== null &&
    (details as { code?: unknown }).code === WORKSPACE_UNAVAILABLE
  );
}

/**
 * Send a request that names the workspace, and when the backend refuses it
 * (the person left that organisation), report it, drop the workspace and send
 * it once more in the default one. For the session check, which must not sign
 * anyone out over a stale choice.
 */
export async function sendInWorkspace(
  send: (headers: Record<string, string>) => Promise<Response>
): Promise<Response> {
  const tenantId = workspaceTenantId;
  const response = await send(workspaceHeaders(tenantId));
  if (!tenantId || response.status !== 403) return response;
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => null);
  if (!isWorkspaceUnavailable(response.status, body)) return response;
  if (workspaceTenantId === tenantId) workspaceTenantId = null;
  notifyWorkspaceUnavailable(tenantId);
  return send({});
}

/** A label for a workspace: its team, else its organisation. */
export function workspaceLabel(workspace: Workspace): string {
  return workspace.teamName || workspace.tenantName;
}

/**
 * The workspace a URL's first segment (the tenant slug of web routes) names,
 * among `workspaces`. Lets a shared link open in the team it belongs to.
 */
export function workspaceForSlug(
  workspaces: readonly Workspace[] | null | undefined,
  slug: string | null | undefined
): Workspace | null {
  if (!slug || !workspaces) return null;
  return workspaces.find(workspace => workspace.tenantSlug === slug) ?? null;
}
